import { request } from '../request';

import i18n from '../../i18n';

export type UserFeedback = 'positive' | 'negative' | 'neutral';

export type TrialOverviewParams = {
  site_ids: string[];
  scheduled_visit_ids: string[];
  trait_ids: string[];
  test_subject_ids: string[];
  include_deleted: boolean;
};

export interface AiContext {
  source: string;
  params: Record<string, unknown>;
}

export interface AiQueryRequest {
  conversation_id: number;
  query: string;
  context: AiContext;
  // Id of a previous model message to regenerate; the server re-answers its
  // question in place instead of appending a new turn.
  regenerate_message_id?: number | null;
}

export interface AiConversation {
  id: number;
}

export interface AiQueryResponse {
  id: number;
  answer: string;
}

/** Error body returned by the message API (e.g. the context is too large). */
interface AiQueryErrorResponse {
  error: string;
}

function instanceOfAiQueryResponse(object: any): object is AiQueryResponse {
  return object != null && 'answer' in object;
}

function instanceOfAiQueryErrorResponse(object: any): object is AiQueryErrorResponse {
  return object != null && typeof object.error === 'string';
}

/**
 * Processes a natural language query about trial data using AI
 *
 * @param data Query and page context for the AI to process
 * @returns Promise with the AI response
 */
export async function processAiQuery(data: AiQueryRequest): Promise<AiQueryResponse> {
  const response = await request<AiQueryResponse | AiQueryErrorResponse>(
    'POST',
    '/api/v2/ai/messages/',
    data,
    {
      injectTenant: true,
      version: 2,
      skipErrorResponseHandling: true,
    }
  );

  if (instanceOfAiQueryResponse(response)) {
    return response;
  }

  return {
    id: null,
    answer: instanceOfAiQueryErrorResponse(response)
      ? response.error
      : i18n.t(
        "I'm sorry, I encountered an error while processing your request. Please try again later."
      )(),
  };
}

export async function createConversation(trialId: number): Promise<AiConversation> {
  const data = {
    trial_id: trialId,
  };

  const response = await request<AiConversation>('POST', '/api/v2/ai/conversations/', data, {
    injectTenant: true,
    version: 2,
    skipErrorResponseHandling: true,
  });

  return response;
}

export async function sendMessageFeedback(messageId: number, feedback: UserFeedback | null): Promise<void> {
  const data = {
    feedback,
  };

  await request('PATCH', `/api/v2/ai/messages/${messageId}/`, data, {
    injectTenant: true,
    version: 2,
    skipErrorResponseHandling: true,
  });
}
